import { Grid, Paper, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";

import { CustomInputField, CustomSelect } from "@/features/ui";
import {
  ChartOfAccountTypeEnum,
  chartOfAccountTypeLabels,
} from "@/features/chart-of-accounts";
import { OutletParentChartOfAccountSelect } from "@/features/outlet-chart-of-accounts";

export const GeneralInformationSection = ({
  outletId,
  hasParent,
  selectedType,
  excludeSubtreeId,
  isEditMode,
}) => {
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Informasi Umum
      </Typography>

      <Grid container spacing={3}>
        {/* Kode */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <CustomInputField
            required
            name="code"
            label="Kode"
            register={register}
            helperText={errors.code?.message}
            error={Boolean(errors.code)}
          />
        </Grid>

        {/* Nama */}
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <CustomInputField
            required
            name="name"
            label="Nama"
            register={register}
            helperText={errors.name?.message}
            error={Boolean(errors.name)}
          />
        </Grid>

        {/* Status */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            control={control}
            name="isActive"
            render={({ field, fieldState }) => (
              <CustomSelect
                required
                value={field.value}
                onChange={field.onChange}
                id="isActive"
                label="Status"
                options={[
                  { value: true, label: "Aktif" },
                  { value: false, label: "Nonaktif" },
                ]}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        {/* Tipe Akun */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <CustomSelect
                required
                id="type"
                label="Tipe Akun"
                options={Object.values(ChartOfAccountTypeEnum).map((value) => ({
                  value: value,
                  label: chartOfAccountTypeLabels[value],
                }))}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  // Clear parent selection when type changes (valid parents differ by type)
                  setValue("parentChartOfAccount", null);
                }}
                helperText={errors.type?.message}
                error={Boolean(errors.type)}
                disabled={isEditMode}
              />
            )}
          />
        </Grid>

        {/* Merupakan Sub */}
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Controller
            control={control}
            name="hasParent"
            render={({ field, fieldState }) => (
              <CustomSelect
                required
                value={field.value}
                onChange={field.onChange}
                id="hasParent"
                label="Merupakan Sub"
                options={[
                  { value: true, label: "Ya" },
                  { value: false, label: "Tidak" },
                ]}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        {/* Turunan dari - only show when hasParent is true */}
        {hasParent && (
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <Controller
              control={control}
              name="parentChartOfAccount"
              render={({ field, fieldState }) => (
                <OutletParentChartOfAccountSelect
                  outletId={outletId}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!selectedType}
                  accountType={selectedType}
                  excludeSubtreeId={excludeSubtreeId}
                  TextFieldProps={{
                    required: hasParent,
                    label: "Turunan dari",
                    error: fieldState.invalid,
                    helperText: !selectedType
                      ? "Pilih tipe akun terlebih dahulu"
                      : fieldState.error?.message,
                    InputProps: {
                      placeholder: "Pilih Akun",
                    },
                  }}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  outletId: PropTypes.string.isRequired,
  hasParent: PropTypes.bool.isRequired,
  selectedType: PropTypes.string,
  excludeSubtreeId: PropTypes.string,
  isEditMode: PropTypes.bool,
};
